import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'

export interface AthleteRow {
  id: string
  name: string
  current_week: number | null
  program_template: string | null
  vald_profile_id: string | null
  email: string | null
  archived_at: string | null
}

/**
 * Reads the real athlete roster. Works only for a signed-in coach — the
 * is_coach() RLS policy grants full read; otherwise PostgREST returns just the
 * caller's own row (or nothing). `enabled` lets callers gate on a session.
 */
export function useAthletes(enabled: boolean, opts: { includeArchived?: boolean } = {}) {
  const q = useQuery({
    queryKey: ['athletes'],
    enabled: enabled && supabaseReady,
    queryFn: async (): Promise<AthleteRow[]> => {
      const { data, error } = await supabase
        .from('athletes')
        .select('id,name,current_week,program_template,vald_profile_id,email,archived_at')
        .order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as AthleteRow[]
    },
  })
  // Archived athletes are hidden everywhere unless a caller explicitly asks.
  const data = useMemo(
    () => (opts.includeArchived ? q.data : q.data?.filter((a) => !a.archived_at)),
    [q.data, opts.includeArchived]
  )
  return { ...q, data }
}

export interface ActiveAssignment {
  programId: string
  programName: string
  currentWeek: number
}

/** Every athlete's current program, keyed by athlete id. One query for the roster. */
export function useActiveAssignments(enabled: boolean) {
  return useQuery({
    queryKey: ['active-assignments'],
    enabled: enabled && supabaseReady,
    queryFn: async (): Promise<Record<string, ActiveAssignment>> => {
      const { data, error } = await supabase
        .from('athlete_programs')
        .select('athlete_id, current_week, program:programs(id, name)')
        .eq('active', true)
      if (error) throw error
      const map: Record<string, ActiveAssignment> = {}
      for (const r of (data ?? []) as unknown as {
        athlete_id: string; current_week: number; program: { id: string; name: string } | null
      }[]) {
        if (r.program) {
          map[r.athlete_id] = { programId: r.program.id, programName: r.program.name, currentWeek: r.current_week }
        }
      }
      return map
    },
  })
}
